import * as THREE from 'three';
import { basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Ser Edda, the Sworn (docs/ANTIHEROES.md): she swore not to let the chapel fall, and the chapel fell. The titan
 * dreams her in fluted plate of black glass cracked through with a cold white light (never gold), a closed helm with
 * two swept wings of glass and a long braid hanging from it, two pale eyes in the slit; on her left arm a tall kite
 * shield with the chapel's star split down the middle, glowing in the split; in her right a flanged mace whose head
 * burns white. A tabard with the same star, and a mantle of smoke.
 *
 * Built on Ser Corvin's bones (the fallen sentinel's) so anim/creatures/edda.ts reuses their walk, reel and fall:
 * body, chest, head, flapF/flapB (the tabard), armL/foreL/shield, armR/foreR/sword (the mace), legs. Faces +Z.
 */

/** Model height (the wings' tips). */
export const EDDA_HEIGHT = 2.3;

export const edda = (o?: BaseOpts): Model => {
  const c = begin(o, 0.64, 1213);
  const { g, rng } = c;
  const k4 = kit(rng);

  const glass = metal('#2c303c', { roughness: 0.14, metalness: 0.55, bumpScale: 0.01 });
  const glassD = metal('#1a1d26', { roughness: 0.26, metalness: 0.5, bumpScale: 0.02 });
  const mail = std('#16181e', { roughness: 0.65, metalness: 0.35, bumpScale: 0.06 });
  const tabard = cloth('#d8dde8', { side: THREE.DoubleSide });
  const tabardD = cloth('#2a2e3a', { side: THREE.DoubleSide });
  const steel = metal('#4a4f5a', { roughness: 0.45, metalness: 0.7, bumpScale: 0.03 });
  const voidM = std('#06070a', { roughness: 1 });
  const light = emis('#ffffff', '#b8cdff', 2.6);
  const lightF = emis('#ffffff', '#dfe8ff', 2.0, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#2a2e3a', 0.5);
  const hairM = basic('#1d2029', 0.85);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: fluted glass cuisses and greaves, knee cops with a cold light in them ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.14, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.14, 1.0, 0), V(k * 0.15, 0.58, 0.02), 0.12, 0.09, glass, 7);
    k4.crack(hip, [V(k * 0.18, 0.94, 0.09), V(k * 0.2, 0.8, 0.1), V(k * 0.17, 0.68, 0.1)], 0.007, light, 0.03);
    const shinB = bone(hip, V(k * 0.15, 0.56, 0.02), `shin${s}`);
    const lo = shinB.abs;
    const cop = k4.swell(lo, glassD, k * 0.15, 0.56, 0.07, 0.08, 0.08, 0.07, 7, 4);
    cop.rotation.x = -0.3;
    M(new THREE.OctahedronGeometry(0.025, 0), light, lo).position.set(k * 0.15, 0.56, 0.14);
    limb(lo, V(k * 0.15, 0.54, 0.02), V(k * 0.15, 0.13, 0.0), 0.095, 0.072, glass, 7);
    const sab = M(new THREE.BoxGeometry(0.15, 0.09, 0.25), glassD, lo);
    sab.position.set(k * 0.15, 0.05, 0.05);
    const toe = M(new THREE.ConeGeometry(0.066, 0.14, 4), glassD, lo);
    toe.position.set(k * 0.15, 0.04, 0.21);
    toe.rotation.set(Math.PI / 2, Math.PI / 4, 0);
    toe.scale.set(1, 1, 0.55);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: mail, glass faulds, the tabard's front (the split star) and back ---------- */
  const bodyB = bone(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.24, 0.31, 0.3, 9, 1, true), mail, body).position.set(0, 0.86, 0);
  const fauld = M(new THREE.CylinderGeometry(0.23, 0.28, 0.18, 9), glassD, body);
  fauld.position.set(0, 1.04, 0);
  const belt = M(new THREE.TorusGeometry(0.24, 0.022, 3, 12), steel, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.12, 0);
  belt.scale.set(1, 0.8, 1);
  const flapF = bone(body, V(0, 1.1, 0.2), 'flapF');
  k4.panel(flapF.abs, V(0, 1.11, 0.23), 0.3, 0.8, 0.02, 1, tabard, 0.35);
  k4.crack(flapF.abs, [V(0, 1.02, 0.245), V(0.01, 0.88, 0.245), V(-0.01, 0.74, 0.24)], 0.009, light, 0.03);
  const flapB = bone(body, V(0, 1.1, -0.2), 'flapB');
  k4.panel(flapB.abs, V(0, 1.11, -0.23), 0.34, 0.86, 0.02, -1, tabardD, 0.45);

  /* ---------- chest: a fluted breastplate, narrower at the waist; the tabard's star; rounded pauldrons ---------- */
  const chestB = bone(body, V(0, 1.16, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.42, 0.0, 0.28, 0.3, 0.2, 9, 6);
  k4.swell(chest, glassD, 0, 1.22, 0.01, 0.23, 0.12, 0.18, 8, 4);
  // Flutes down the breastplate.
  for (const x of [-0.1, 0, 0.1]) {
    const fl = M(new THREE.BoxGeometry(0.018, 0.3, 0.02), glassD, chest);
    fl.position.set(x, 1.42, 0.2 - Math.abs(x) * 0.25);
  }
  // The chapel's star on her breast, split down the middle; light in the split.
  const star = G(chest, 0, 1.45, 0.215);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const ray = M(new THREE.BoxGeometry(0.016, i % 2 ? 0.07 : 0.12, 0.012), steel, star);
    ray.position.set(Math.sin(a) * 0.05, Math.cos(a) * 0.05, 0);
    ray.rotation.z = -a;
  }
  M(new THREE.BoxGeometry(0.012, 0.22, 0.016), light, star);
  glowSprite(chest, '#dfe8ff', 0.4, V(0, 1.45, 0.26), 0.55);
  k4.crack(chest, [V(0.18, 1.6, 0.15), V(0.1, 1.52, 0.2), V(0.04, 1.47, 0.215)], 0.009, light, 0.03);
  M(new THREE.CylinderGeometry(0.11, 0.14, 0.09, 8), glassD, chest).position.set(0, 1.64, 0);
  for (const k of [-1, 1]) {
    const pd = G(chest, k * 0.33, 1.62, 0);
    pd.rotation.z = -k * 0.3;
    const dome = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), glass, pd);
    dome.scale.set(0.19, 0.14, 0.19);
    for (let i = 1; i <= 2; i++) {
      const lame = M(new THREE.CylinderGeometry(0.19 - i * 0.01, 0.2 - i * 0.008, 0.05, 8, 1, true), i === 1 ? glassD : glass, pd);
      lame.position.y = -0.035 - i * 0.045;
    }
    M(new THREE.TorusGeometry(0.17, 0.008, 3, 12, Math.PI), light, pd).rotation.set(0, Math.PI / 2, 0);
  }

  /* ---------- the mantle of smoke ---------- */
  const mantle = G(chest, 0, 1.64, -0.19);
  for (const [x, w, h, rz] of [[-0.16, 0.28, 1.25, 0.06], [0.16, 0.28, 1.2, -0.06], [0, 0.32, 1.35, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.6);
    pn.rotation.set(0.12, 0, rz);
  }

  /* ---------- head: a closed helm, a cross slit with two pale eyes, two swept wings of glass, a braid ---------- */
  const headB = bone(chest, V(0, 1.68, 0.01), 'head');
  const head = headB.abs;
  M(new THREE.CylinderGeometry(0.125, 0.135, 0.27, 10), glass, head).position.set(0, 1.82, 0.01);
  const dome = M(new THREE.SphereGeometry(0.128, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2), glass, head);
  dome.position.set(0, 1.955, 0.01);
  dome.scale.set(1, 1.15, 1);
  const slit = M(new THREE.BoxGeometry(0.2, 0.026, 0.03), voidM, head);
  slit.position.set(0, 1.86, 0.128);
  const slitV = M(new THREE.BoxGeometry(0.03, 0.14, 0.03), voidM, head);
  slitV.position.set(0, 1.8, 0.128);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.022, 6, 4), light, head).position.set(k * 0.05, 1.86, 0.135);
    glowSprite(head, '#dfe8ff', 0.22, V(k * 0.05, 1.86, 0.17), 0.95);
  }
  const ridge = M(new THREE.BoxGeometry(0.022, 0.05, 0.26), glassD, head);
  ridge.position.set(0, 2.08, -0.01);
  // The wings: three glass feathers each side, swept up and back, a cold edge on the longest.
  for (const k of [-1, 1]) {
    const wing = G(head, k * 0.12, 1.93, -0.02);
    wing.rotation.set(-0.35, k * 0.25, -k * 0.55);
    for (let i = 0; i < 4; i++) {
      const len = 0.52 - i * 0.1;
      const dir = V(k * (0.35 + i * 0.12), 1 - i * 0.2, -0.45 - i * 0.2);
      const f = k4.shard(wing, i % 2 ? glassD : glass, V(0, -i * 0.04, -i * 0.03), dir, len, 0.075);
      f.scale.z = 0.4;
      // A cold edge along each feather.
      const e = dir.clone().normalize().multiplyScalar(len * 0.9);
      k4.crack(wing, [V(0, -i * 0.04 + 0.01, -i * 0.03), V(e.x * 0.5, -i * 0.04 + e.y * 0.5, -i * 0.03 + e.z * 0.5), V(e.x, -i * 0.04 + e.y, -i * 0.03 + e.z)], 0.006, light, 0.005);
    }
  }
  // The braid down her back, from under the helm, bound with a ring of steel.
  const braid = k4.tube(head, [V(0, 1.86, -0.12), V(0.02, 1.72, -0.19), V(-0.01, 1.56, -0.22), V(0.02, 1.4, -0.24), V(0, 1.28, -0.25)], 0.032, hairM);
  braid.castShadow = false;
  M(new THREE.TorusGeometry(0.035, 0.01, 3, 8), steel, head).position.set(0.015, 1.4, -0.24);

  /* ---------- the shield arm: a tall kite shield of glass, the star split down it, light in the split ---------- */
  const armLB = bone(chest, V(-0.33, 1.56, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.34, 1.56, 0), V(-0.38, 1.24, 0.05), 0.08, 0.068, glass, 6);
  const foreLB = bone(aL, V(-0.38, 1.23, 0.06), 'foreL');
  const fL = foreLB.abs;
  k4.swell(fL, glassD, -0.38, 1.23, 0.06, 0.068, 0.068, 0.068, 6, 4);
  limb(fL, V(-0.38, 1.23, 0.06), V(-0.25, 1.18, 0.28), 0.066, 0.055, glass, 6);
  const shieldB = bone(fL, V(-0.23, 1.17, 0.32), 'shield');
  const sh = shieldB.abs;
  {
    const sg = G(sh, -0.1, 1.0, 0.4);
    sg.rotation.set(-0.05, 0.12, 0);
    // A kite: wide at the top, a point at the bottom (an extruded outline).
    const kite = new THREE.Shape();
    kite.moveTo(-0.3, 0.42);
    kite.quadraticCurveTo(0, 0.5, 0.3, 0.42);
    kite.lineTo(0.27, -0.1);
    kite.quadraticCurveTo(0.15, -0.5, 0, -0.72);
    kite.quadraticCurveTo(-0.15, -0.5, -0.27, -0.1);
    kite.lineTo(-0.3, 0.42);
    const geo = new THREE.ExtrudeGeometry(kite, { depth: 0.05, bevelEnabled: true, bevelThickness: 0.015, bevelSize: 0.02, bevelSegments: 1, curveSegments: 5 });
    geo.translate(0, 0, -0.025);
    M(geo, glass, sg);
    // Its rim of steel and the star across its face, split by a crack of light top to point.
    const rim = M(new THREE.TorusGeometry(0.29, 0.016, 3, 16, Math.PI), steel, sg);
    rim.position.set(0, 0.2, 0.05);
    rim.scale.set(1.02, 0.5, 1);
    const st = G(sg, 0, 0.08, 0.055);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const len = i % 2 ? 0.12 : 0.22;
      const ray = M(new THREE.BoxGeometry(0.03, len, 0.015), steel, st);
      ray.position.set(Math.sin(a) * (0.05 + len / 2), Math.cos(a) * (0.05 + len / 2), 0);
      ray.rotation.z = -a;
    }
    M(new THREE.CylinderGeometry(0.05, 0.05, 0.02, 10), steel, st).rotation.x = Math.PI / 2;
    k4.crack(sg, [V(0.02, 0.44, 0.07), V(-0.02, 0.24, 0.075), V(0.03, 0.06, 0.08), V(-0.02, -0.2, 0.075), V(0.01, -0.45, 0.07), V(0, -0.66, 0.06)], 0.013, light, 0.04);
    glowSprite(sg, '#dfe8ff', 0.6, V(0, 0.05, 0.12), 0.55);
  }
  M(new THREE.BoxGeometry(0.08, 0.09, 0.09), glassD, sh).position.set(-0.23, 1.17, 0.32);

  /* ---------- the mace arm: a flanged mace of steel and glass, its head burning white ---------- */
  const armRB = bone(chest, V(0.33, 1.56, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.34, 1.56, 0), V(0.4, 1.23, 0.02), 0.08, 0.068, glass, 6);
  const foreRB = bone(aR, V(0.4, 1.22, 0.02), 'foreR');
  const fR = foreRB.abs;
  k4.swell(fR, glassD, 0.4, 1.22, 0.02, 0.068, 0.068, 0.068, 6, 4);
  limb(fR, V(0.4, 1.22, 0.02), V(0.42, 0.95, 0.1), 0.066, 0.056, glass, 6);
  const maceB = bone(fR, V(0.42, 0.91, 0.11), 'sword');
  const mc = maceB.abs;
  M(new THREE.BoxGeometry(0.085, 0.095, 0.095), glassD, mc).position.set(0.42, 0.91, 0.11);
  const flames: THREE.Mesh[] = [];
  {
    const m = G(mc, 0.42, 0.91, 0.11);
    m.rotation.x = 0.35;
    M(new THREE.CylinderGeometry(0.022, 0.022, 0.7, 6), steel, m).position.y = 0.2;
    M(new THREE.SphereGeometry(0.035, 6, 4), steel, m).position.y = -0.17;
    const hd = G(m, 0, 0.6, 0);
    M(new THREE.SphereGeometry(0.075, 8, 6), glassD, hd);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const fl = M(new THREE.BoxGeometry(0.03, 0.17, 0.09), glass, hd);
      fl.position.set(Math.sin(a) * 0.075, 0, Math.cos(a) * 0.075);
      fl.rotation.y = a;
      const edge = M(new THREE.BoxGeometry(0.01, 0.15, 0.014), light, hd);
      edge.position.set(Math.sin(a) * 0.122, 0, Math.cos(a) * 0.122);
      edge.rotation.y = a;
    }
    M(new THREE.SphereGeometry(0.035, 6, 4), steel, hd).position.y = 0.11;
    for (const [x, z, s, h] of [[0, 0, 1, 0.16], [0.03, -0.02, 0.7, 0.12]] as const) {
      const f = M(new THREE.ConeGeometry(0.04 * s, h, 6), lightF, hd);
      f.position.set(x, 0.12 + h / 2, z);
      flames.push(f);
    }
    glowSprite(hd, '#dfe8ff', 0.55, V(0, 0.04, 0.03), 0.6);
  }

  /* ---------- smoke breathing off her ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.3, -0.2, 0.65, 1.55], [-0.26, -0.22, 0.6, 1.5], [0.05, -0.3, 0.8, 1.3], [-0.12, -0.28, 0.55, 0.9]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.05, smokeM, rng, 0.06));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    flames.forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.5 * k, 0.85 + 0.3 * k);
    });
  };
  t(0);
  return done(c, EDDA_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: maceB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
